// Turns an Excel workbook (.xlsx) into CSV in the browser, so owners can upload
// a back-office or wholesaler export exactly as they downloaded it. Only the
// first worksheet is read. Dates become YYYY-MM-DD and numbers keep their full
// digits (no 6.11E+11 barcodes), which is where "Save as CSV" usually goes wrong.

const EXCEL_FILE = /\.(xlsx|xlsm)$/i;
const OLD_EXCEL_FILE = /\.xls$/i;

export const UPLOAD_ACCEPT = '.csv,.tsv,.txt,.xlsx,.xlsm,.xls,.xml,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

// The file to upload: CSV/TSV/XML as-is, Excel converted to CSV.
export async function toUploadableFile(file: File): Promise<File> {
  if (OLD_EXCEL_FILE.test(file.name)) throw new Error('This is an old Excel (.xls) file. Open it in Excel and choose File → Save As → Excel Workbook (.xlsx) or CSV, then upload that.');
  if (!EXCEL_FILE.test(file.name)) return file;
  const csv = await xlsxToCsv(await file.arrayBuffer());
  return new File([csv], file.name.replace(EXCEL_FILE, '.csv'), { type: 'text/csv' });
}

// ---- zip ----

// In every current browser (Chrome 80, Safari 16.4, Firefox 113); missing from this TypeScript's DOM types.
declare const DecompressionStream: { new (format: 'deflate-raw'): TransformStream<Uint8Array, Uint8Array> };

interface ZipEntry { method: number; compressedSize: number; offset: number }

function zipEntries(bytes: Uint8Array): Map<string, ZipEntry> {
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  let end = bytes.length - 22;
  while (end >= 0 && view.getUint32(end, true) !== 0x06054b50) end -= 1;
  if (end < 0) throw new Error('This Excel file could not be opened. Save it again in Excel, or export it as CSV.');
  const count = view.getUint16(end + 10, true);
  let position = view.getUint32(end + 16, true);
  const entries = new Map<string, ZipEntry>();
  for (let index = 0; index < count; index += 1) {
    if (view.getUint32(position, true) !== 0x02014b50) break;
    const nameLength = view.getUint16(position + 28, true);
    const name = new TextDecoder().decode(bytes.subarray(position + 46, position + 46 + nameLength));
    entries.set(name, { method: view.getUint16(position + 10, true), compressedSize: view.getUint32(position + 20, true), offset: view.getUint32(position + 42, true) });
    position += 46 + nameLength + view.getUint16(position + 30, true) + view.getUint16(position + 32, true);
  }
  return entries;
}

async function readEntry(bytes: Uint8Array, entries: Map<string, ZipEntry>, name: string): Promise<string | null> {
  const entry = entries.get(name);
  if (!entry) return null;
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  const start = entry.offset + 30 + view.getUint16(entry.offset + 26, true) + view.getUint16(entry.offset + 28, true);
  const data = bytes.slice(start, start + entry.compressedSize);
  if (entry.method === 0) return new TextDecoder().decode(data);
  if (entry.method !== 8) throw new Error('This Excel file uses a format Pellvr cannot read. Export it as CSV instead.');
  const reader = new ReadableStream<Uint8Array>({ start(controller) { controller.enqueue(data); controller.close(); } })
    .pipeThrough(new DecompressionStream('deflate-raw')).getReader();
  const decoder = new TextDecoder(); let text = '';
  for (let chunk = await reader.read(); !chunk.done; chunk = await reader.read()) text += decoder.decode(chunk.value, { stream: true });
  return text + decoder.decode();
}

// ---- workbook ----

const xml = (text: string) => new DOMParser().parseFromString(text, 'application/xml');
const byTag = (node: Document | Element, tag: string) => Array.from(node.getElementsByTagNameNS('*', tag));
const textOf = (node: Element) => byTag(node, 't').map(element => element.textContent ?? '').join('');

// Built-in date formats (14-22, 45-47) and custom codes with day/month/year parts.
function dateStyles(stylesXml: string | null): Set<number> {
  const result = new Set<number>();
  if (!stylesXml) return result;
  const doc = xml(stylesXml);
  const custom = new Map(byTag(doc, 'numFmt').map(format => [Number(format.getAttribute('numFmtId')), format.getAttribute('formatCode') ?? '']));
  const isDateFormat = (id: number) => (id >= 14 && id <= 22) || (id >= 45 && id <= 47)
    || /[dmy]/i.test((custom.get(id) ?? '').replace(/"[^"]*"|\[[^\]]*\]|\\./g, ''));
  const cellXfs = byTag(doc, 'cellXfs')[0];
  if (cellXfs) Array.from(cellXfs.children).forEach((xf, index) => { if (isDateFormat(Number(xf.getAttribute('numFmtId')))) result.add(index); });
  return result;
}

function excelDate(serial: number, date1904: boolean): string {
  const ms = Math.round((serial + (date1904 ? 1462 : 0)) * 86400000) + Date.UTC(1899, 11, 30);
  const iso = new Date(ms).toISOString();
  return iso.endsWith('T00:00:00.000Z') ? iso.slice(0, 10) : iso.slice(0, 19).replace('T', ' ');
}

function columnIndex(reference: string | null, fallback: number): number {
  const letters = reference?.match(/^[A-Z]+/i)?.[0];
  if (!letters) return fallback;
  return letters.toUpperCase().split('').reduce((total, letter) => total * 26 + letter.charCodeAt(0) - 64, 0) - 1;
}

const csvCell = (value: string) => /[",\r\n]/.test(value) ? `"${value.replace(/"/g, '""')}"` : value;

export async function xlsxToCsv(buffer: ArrayBuffer): Promise<string> {
  const bytes = new Uint8Array(buffer);
  const entries = zipEntries(bytes);
  const workbookXml = await readEntry(bytes, entries, 'xl/workbook.xml');
  if (!workbookXml) throw new Error('This file is not an Excel workbook. Export it as CSV instead.');
  const workbook = xml(workbookXml);
  const date1904 = byTag(workbook, 'workbookPr')[0]?.getAttribute('date1904') === '1';
  const firstSheet = byTag(workbook, 'sheet')[0];
  const relationId = firstSheet?.getAttributeNS('http://schemas.openxmlformats.org/officeDocument/2006/relationships', 'id') ?? firstSheet?.getAttribute('r:id');
  const rels = xml((await readEntry(bytes, entries, 'xl/_rels/workbook.xml.rels')) ?? '<Relationships/>');
  const target = byTag(rels, 'Relationship').find(rel => rel.getAttribute('Id') === relationId)?.getAttribute('Target') ?? 'worksheets/sheet1.xml';
  const sheetPath = target.startsWith('/') ? target.slice(1) : `xl/${target.replace(/^\.\//, '')}`;
  const sheetXml = await readEntry(bytes, entries, sheetPath);
  if (!sheetXml) throw new Error('The first sheet of this workbook is empty. Move your data to the first sheet, or export it as CSV.');

  const sharedXml = await readEntry(bytes, entries, 'xl/sharedStrings.xml');
  const shared = sharedXml ? byTag(xml(sharedXml), 'si').map(textOf) : [];
  const dates = dateStyles(await readEntry(bytes, entries, 'xl/styles.xml'));

  const lines: string[] = [];
  for (const row of byTag(xml(sheetXml), 'row')) {
    const cells: string[] = [];
    byTag(row, 'c').forEach((cell, position) => {
      const type = cell.getAttribute('t');
      const raw = byTag(cell, 'v')[0]?.textContent ?? '';
      let value: string;
      if (type === 's') value = shared[Number(raw)] ?? '';
      else if (type === 'inlineStr') value = textOf(cell);
      else if (type === 'b') value = raw === '1' ? 'TRUE' : 'FALSE';
      else if (type === 'str' || type === 'e' || type === 'd') value = raw;
      else if (raw === '') value = '';
      else if (dates.has(Number(cell.getAttribute('s') ?? -1))) value = excelDate(Number(raw), date1904);
      else value = String(parseFloat(Number(raw).toPrecision(15)));
      cells[columnIndex(cell.getAttribute('r'), position)] = value;
    });
    const filled = Array.from(cells, value => value ?? '');
    if (filled.some(value => value.trim() !== '')) lines.push(filled.map(csvCell).join(','));
  }
  if (!lines.length) throw new Error('The first sheet of this workbook is empty. Move your data to the first sheet, or export it as CSV.');
  return `${lines.join('\r\n')}\r\n`;
}
